import i18n from '@/i18n/i18n'

import * as ApprovalService from '@/services/admin/ApprovalService'
import { PermissionService } from '@/services/admin/PermissionService'
import { build_query, errorMessage } from '@/utilities/helpers'

import { DUAL_GUARDED_ACTION_STATUS, DUAL_GUARDED_ARCHIVED_STATUSES } from '@/constants/dualGuardedActionStatus'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = ['status', 'user_id', 'permission_id']

export const allowedIncludes = ''

export const allowedSorts = ['created_at', 'reviewed_at']

export const state = {
  approvals: [],
  approval: {},
  meta: null,
  approvablePermissions: []
}

export const mutations = {
  SET_APPROVALS(state, approvals) {
    state.approvals = approvals.data
    state.meta = approvals.meta
  },
  SET_APPROVAL(state, approval) {
    state.approval = approval.data
  },
  REMOVE_APPROVAL(state, id) {
    state.approvals = state.approvals.filter(approval => approval.id !== id)
  },
  CLEAR_APPROVALS(state) {
    state.approvals = []
    state.meta = null
  },
  SET_APPROVABLE_PERMISSIONS(state, permissions) {
    state.approvablePermissions = permissions.data
  }
}

export const actions = {
  getPendingApprovals({ commit, dispatch }, query) {
    let newQuery = build_query(
      {
        ...query,
        status: DUAL_GUARDED_ACTION_STATUS.PENDING,
        sort: '-created_at'
      },
      allowedFilters
    )

    commit('CLEAR_APPROVALS')

    return new Promise((resolve, reject) => {
      ApprovalService.getApprovals(newQuery)
        .then(response => {
          commit('SET_APPROVALS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: `${t('store.shared.fetching_problem', {
              element: t('store.admin.approvals.pending_approvals')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getArchivedApprovals({ commit, dispatch }, query) {
    let newQuery = build_query(
      { ...query, status: DUAL_GUARDED_ARCHIVED_STATUSES.join(','), sort: '-created_at' },
      allowedFilters,
      allowedIncludes,
      allowedSorts
    )

    commit('CLEAR_APPROVALS')

    return new Promise((resolve, reject) => {
      ApprovalService.getApprovals(newQuery)
        .then(response => {
          commit('SET_APPROVALS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: `${t('store.shared.fetching_problem', {
              element: t('store.admin.approvals.archived_approvals')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApproval({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      ApprovalService.getApproval(id)
        .then(response => {
          commit('SET_APPROVAL', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: `${t('store.shared.fetching_problem', {
              element: t('store.admin.approvals.approval_details')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  update({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      ApprovalService.update(
        params.id,
        params.decision,
        params.reviewNotes,
        params.shouldNotifyExpiredResidenceCertificate ?? null
      )
        .then(response => {
          let notification = {}

          switch (response.data.status) {
            case DUAL_GUARDED_ACTION_STATUS.APPROVED: {
              notification = {
                type: 'success',
                message: t('store.admin.approvals.approved')
              }
              break
            }
            case DUAL_GUARDED_ACTION_STATUS.REJECTED: {
              notification = {
                type: 'success',
                message: t('store.admin.approvals.rejected')
              }
              break
            }
          }

          commit('REMOVE_APPROVAL', params.id)

          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: `${t('store.shared.update_error', {
              element: t('store.admin.approvals.pending_approval')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApprovablePermissions({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      let permissionService = new PermissionService({ 'filter[approvable]': true })

      permissionService
        .getPermissions()
        .then(response => {
          commit('SET_APPROVABLE_PERMISSIONS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'warning',
            message: `${t('store.shared.fetching_problem', {
              element: t('store.admin.approvals.approvable_permissions')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
